import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Snackbar,
  Switch,
  TextField,
} from "@mui/material";
import { MdCancel, MdOutlineFileUpload } from "react-icons/md";
import Breadcumbs from "../../common/particles.jsx/Breadcumbs";
import {
  getPSEFSetting,
  listPSEFCourier,
  updatePSEFSetting,
} from "../../../services/PSEFSettingApi";
import { uploadMediaOms } from "../../../services/oms";
import { useMutation, useQuery } from "@tanstack/react-query";

const MAX_FILE_SIZE = 10 * 1024 * 1024;
const MAX_BANNERS = 5;
const ALLOWED_IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "webp"];

const emptyForm = {
  logo: "",
  banners: [],
  internal_courier: false,
  internal_courier_radius: "",
  courier_ids: [],
  latitude: "",
  longitude: "",
  contact_cs_name: "",
  whatsapp_cs_number: "",
  consultation_fallback: "",
};

const mapSettingToForm = (dataSetting) => ({
  logo: dataSetting?.logo || "",
  banners: Array.isArray(dataSetting?.banners) ? dataSetting.banners : [],
  internal_courier: Boolean(dataSetting?.internal_courier),
  internal_courier_radius: dataSetting?.internal_courier_radius ?? "",
  courier_ids: Array.isArray(dataSetting?.courier_ids)
    ? dataSetting.courier_ids
    : [],
  latitude: dataSetting?.latitude ?? "",
  longitude: dataSetting?.longitude ?? "",
  contact_cs_name: dataSetting?.contact_cs_name || "",
  whatsapp_cs_number: dataSetting?.whatsapp_cs_number || "",
  consultation_fallback: dataSetting?.consultation_fallback || "",
});

const PSEFSetting = () => {
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isEditMode, setIsEditMode] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);

  const logoInputRef = useRef(null);
  const bannerInputRef = useRef(null);

  const onChangeField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const normalizeWaNumber = (value) => value.replace(/[^\d]/g, "");

  const validateFile = (file) => {
    const fileExtension = file.name.split(".").pop()?.toLowerCase();
    const isImage = ALLOWED_IMAGE_EXTENSIONS.includes(fileExtension);

    if (!isImage) {
      return `Format file tidak didukung. Gunakan: ${ALLOWED_IMAGE_EXTENSIONS.join(
        ", ",
      )}`;
    }

    if (file.size > MAX_FILE_SIZE) {
      return `Ukuran file terlalu besar. Maksimal ${
        MAX_FILE_SIZE / (1024 * 1024)
      } MB`;
    }

    return null;
  };

  const validate = () => {
    const nextErrors = {};

    if (form.internal_courier) {
      if (
        form.internal_courier_radius === "" ||
        Number.isNaN(Number(form.internal_courier_radius)) ||
        Number(form.internal_courier_radius) < 0
      ) {
        nextErrors.internal_courier_radius =
          "Radius wajib diisi dengan angka valid";
      }
    }

    if (!form.whatsapp_cs_number.trim()) {
      nextErrors.whatsapp_cs_number = "Nomor WA wajib diisi";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const { data: dataCouriers = [], isFetching: isFetchingCourier } = useQuery({
    queryKey: ["list-psef-courier"],
    queryFn: () => listPSEFCourier(),
    refetchOnWindowFocus: false,
  });

  const {
    data: dataSetting,
    isFetching: isFetchingSetting,
    refetch,
  } = useQuery({
    queryKey: ["psef-setting"],
    queryFn: () => getPSEFSetting(),
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!dataSetting) return;
    setForm(mapSettingToForm(dataSetting));
  }, [dataSetting]);

  const { mutate: mutateUploadMedia } = useMutation({
    mutationFn: uploadMediaOms,
  });

  const { mutate: mutateUpdate, isLoading } = useMutation({
    mutationFn: updatePSEFSetting,
    onSuccess: () => {
      setSuccessMessage("Berhasil mengupdate PSEF Setting");
      setIsEditMode(false);
      refetch();
    },
    onError: (err) => setErrorMessage(err || "Gagal mengupdate PSEF Setting"),
  });

  const uploadSingleFile = (file) =>
    new Promise((resolve, reject) => {
      const validationError = validateFile(file);
      if (validationError) {
        reject(new Error(validationError));
        return;
      }

      const formData = new FormData();
      formData.append("file", file);
      formData.append("type", "public");
      formData.append("datasource", "psef-setting");

      mutateUploadMedia(formData, {
        onSuccess: (response) => {
          if (response?.status === "success" && response?.detail?.url) {
            resolve(response.detail.url);
            return;
          }

          const fallbackUrl =
            response?.detail?.url ||
            response?.detail?.secure_url ||
            response?.detail?.file_url ||
            response?.url;

          if (fallbackUrl) {
            resolve(fallbackUrl);
            return;
          }

          reject(new Error(response?.message || "Upload logo gagal"));
        },
        onError: (error) => {
          reject(
            new Error(
              error?.response?.data?.message ||
                error?.message ||
                "Terjadi kesalahan saat upload logo",
            ),
          );
        },
      });
    });

  const uploadMultipleFiles = (files) =>
    new Promise((resolve, reject) => {
      const formData = new FormData();
      files.forEach((file) => formData.append("file", file));
      formData.append("type", "public");
      formData.append("datasource", "psef-setting");

      mutateUploadMedia(formData, {
        onSuccess: (response) => {
          if (response?.status === "success") {
            // kemungkinan response.detail berupa array URL
            if (Array.isArray(response?.detail)) {
              resolve(response.detail);
              return;
            }

            // fallback jika single object/url
            if (typeof response?.detail === "string") {
              resolve([response.detail]);
              return;
            }

            if (response?.detail?.url) {
              resolve([response.detail.url]);
              return;
            }
          }

          reject(new Error(response?.message || "Upload banner gagal"));
        },
        onError: (error) => {
          reject(
            new Error(
              error?.response?.data?.message ||
                error?.message ||
                "Terjadi kesalahan saat upload banner",
            ),
          );
        },
      });
    });

  const handleLogoChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = null;
    if (!file) return;

    setUploadingLogo(true);
    setErrorMessage("");

    try {
      const uploadedUrl = await uploadSingleFile(file);
      onChangeField("logo", uploadedUrl);
    } catch (err) {
      setErrorMessage(err.message || "Gagal upload logo");
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleBannerChange = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = null;
    if (files.length === 0) return;

    const remaining = MAX_BANNERS - form.banners.length;
    if (remaining <= 0) {
      setErrorMessage(`Maksimal ${MAX_BANNERS} banner`);
      return;
    }

    const selectedFiles = files.slice(0, remaining);

    const invalidFile = selectedFiles.find((file) => validateFile(file));
    if (invalidFile) {
      setErrorMessage(`${invalidFile.name}: ${validateFile(invalidFile)}`);
      return;
    }

    setUploadingBanner(true);
    setErrorMessage("");

    try {
      let uploadedUrls = [];

      // coba multiple dulu
      try {
        uploadedUrls = await uploadMultipleFiles(selectedFiles);
      } catch (multipleErr) {
        // fallback sequential jika endpoint multiple bermasalah
        uploadedUrls = [];
        for (const file of selectedFiles) {
          // eslint-disable-next-line no-await-in-loop
          const oneUrl = await uploadSingleFile(file);
          uploadedUrls.push(oneUrl);
        }
      }

      onChangeField(
        "banners",
        [...form.banners, ...uploadedUrls].slice(0, MAX_BANNERS),
      );
    } catch (err) {
      setErrorMessage(err.message || "Gagal upload banner");
    } finally {
      setUploadingBanner(false);
    }
  };

  const handleSave = () => {
    if (!validate()) return;

    const payload = {
      logo: form.logo || "",
      banners: Array.isArray(form.banners) ? form.banners.filter(Boolean) : [],
      internal_courier: Boolean(form.internal_courier),
      internal_courier_radius: Number(form.internal_courier_radius) || 0,
      courier_ids: Array.isArray(form.courier_ids) ? form.courier_ids : [],
      latitude: Number(form.latitude) || 0,
      longitude: Number(form.longitude) || 0,
      contact_cs_name: form.contact_cs_name || "",
      whatsapp_cs_number: form.whatsapp_cs_number || "",
      consultation_fallback: form.consultation_fallback || "-",
    };

    mutateUpdate(payload);
  };

  const selectedCourierNames = useMemo(() => {
    return dataCouriers
      .filter((item) => form.courier_ids.includes(item.id))
      .map((item) => item.name)
      .join(", ");
  }, [dataCouriers, form.courier_ids]);

  if (isFetchingSetting) {
    return (
      <div className="container mx-auto w-full mt-16 lg:mt-0">Loading...</div>
    );
  }

  return (
    <div className="container mx-auto w-full mt-16 lg:mt-0">
      <Breadcumbs
        nama1="Setting"
        nama2="PSEF Settings"
        link1="/settings/psef-setting"
        link2="/settings/psef-setting"
      />

      <div className="w-full mt-5 grid grid-cols-1 gap-4">
        <div className="border rounded-lg p-4">
          <p className="font-semibold text-[16px] mb-4">General</p>

          <div className="mt-4">
            <p className="font-medium mb-2">Logo</p>
            {form.logo ? (
              <div className="relative border rounded-lg p-3 w-fit">
                {isEditMode && (
                  <button
                    type="button"
                    onClick={() => onChangeField("logo", "")}
                  >
                    <MdCancel className="text-red-500 text-[24px] absolute -top-3 -right-3 bg-white rounded-full" />
                  </button>
                )}
                <img
                  src={form.logo}
                  alt="Logo"
                  className="w-24 h-24 object-contain"
                />
              </div>
            ) : (
              <p className="text-sm text-gray-500">Belum ada logo</p>
            )}

            {isEditMode && (
              <>
                <input
                  hidden
                  ref={logoInputRef}
                  id="logo-file"
                  type="file"
                  accept="image/jpeg,image/png,image/jpg,image/webp"
                  onChange={handleLogoChange}
                />
                <button
                  type="button"
                  onClick={() => logoInputRef.current?.click()}
                  disabled={uploadingLogo}
                  className="mt-3 flex items-center gap-2 px-4 py-2 border rounded-md hover:bg-gray-50 disabled:opacity-50"
                >
                  <MdOutlineFileUpload className="text-[20px]" />
                  {uploadingLogo
                    ? "Mengupload logo..."
                    : "Upload Logo (max 10MB)"}
                </button>
              </>
            )}
          </div>
        </div>

        <div className="border rounded-lg p-4">
          <p className="font-semibold text-[16px] mb-4">Shipping Settings</p>

          <div className="mb-3">
            <FormControlLabel
              control={
                <Switch
                  checked={form.internal_courier}
                  onChange={(e) =>
                    onChangeField("internal_courier", e.target.checked)
                  }
                  disabled={!isEditMode}
                />
              }
              label="Enable Internal Courier Option"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            <TextField
              label="Radius Setting (meter)"
              type="number"
              value={form.internal_courier_radius}
              onChange={(e) =>
                onChangeField("internal_courier_radius", e.target.value)
              }
              error={Boolean(errors.internal_courier_radius)}
              helperText={errors.internal_courier_radius}
              fullWidth
              disabled={!isEditMode}
            />
            <TextField
              label="Shipping Origin Latitude"
              value={form.latitude}
              onChange={(e) => onChangeField("latitude", e.target.value)}
              fullWidth
              disabled={!isEditMode}
            />
            <TextField
              label="Shipping Origin Longitude"
              value={form.longitude}
              onChange={(e) => onChangeField("longitude", e.target.value)}
              fullWidth
              disabled={!isEditMode}
            />
          </div>

          <TextField
            select
            fullWidth
            label="Courier"
            value=""
            disabled={!isEditMode || isFetchingCourier}
            SelectProps={{ renderValue: () => "Pilih courier" }}
          >
            {dataCouriers.map((courier) => (
              <MenuItem
                key={courier.id}
                value={courier.id}
                onClick={() => {
                  if (!isEditMode) return;
                  const selected = form.courier_ids.includes(courier.id);
                  onChangeField(
                    "courier_ids",
                    selected
                      ? form.courier_ids.filter((id) => id !== courier.id)
                      : [...form.courier_ids, courier.id],
                  );
                }}
              >
                <Checkbox checked={form.courier_ids.includes(courier.id)} />
                {courier.name}
              </MenuItem>
            ))}
          </TextField>
          <div className="mt-2 text-sm text-gray-700">
            {selectedCourierNames || "Belum ada courier dipilih"}
          </div>
        </div>

        <div className="border rounded-lg p-4">
          <p className="font-semibold text-[16px] mb-4">
            Customer Service Settings
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <TextField
              label="Contact Name"
              value={form.contact_cs_name}
              onChange={(e) => onChangeField("contact_cs_name", e.target.value)}
              fullWidth
              disabled={!isEditMode}
            />
            <TextField
              label="Contact Number (WA)"
              value={form.whatsapp_cs_number}
              onChange={(e) =>
                onChangeField(
                  "whatsapp_cs_number",
                  normalizeWaNumber(e.target.value),
                )
              }
              error={Boolean(errors.whatsapp_cs_number)}
              helperText={
                errors.whatsapp_cs_number ||
                "Format angka WA, contoh: 6281234567890"
              }
              fullWidth
              disabled={!isEditMode}
            />
          </div>
        </div>

        <div className="border rounded-lg p-4">
          <p className="font-semibold text-[16px] mb-4">
            Consultation Settings
          </p>
          <TextField
            label="Consultation Fallback Text"
            value={form.consultation_fallback}
            onChange={(e) =>
              onChangeField("consultation_fallback", e.target.value)
            }
            fullWidth
            multiline
            minRows={3}
            disabled={!isEditMode}
          />
        </div>

        <div className="border rounded-lg p-4">
          <p className="font-semibold text-[16px] mb-4">Banner</p>
          <p className="text-sm text-gray-500 mb-3">
            Maksimal {MAX_BANNERS} banner • Format JPG/PNG/WEBP • Maks. 10MB per
            file
          </p>

          {form.banners.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {form.banners.map((bannerUrl, index) => (
                <div
                  key={`${bannerUrl}-${index}`}
                  className="relative border rounded-lg p-2"
                >
                  {isEditMode && (
                    <button
                      type="button"
                      onClick={() =>
                        onChangeField(
                          "banners",
                          form.banners.filter((_, i) => i !== index),
                        )
                      }
                    >
                      <MdCancel className="text-red-500 text-[22px] absolute -top-2 -right-2 bg-white rounded-full" />
                    </button>
                  )}
                  <img
                    src={bannerUrl}
                    alt={`Banner-${index + 1}`}
                    className="w-full h-24 object-cover rounded-md"
                  />
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">Belum ada banner</p>
          )}

          {isEditMode && (
            <>
              <input
                hidden
                ref={bannerInputRef}
                id="banner-file"
                type="file"
                multiple
                accept="image/jpeg,image/png,image/jpg,image/webp"
                onChange={handleBannerChange}
              />
              <button
                type="button"
                onClick={() => bannerInputRef.current?.click()}
                disabled={uploadingBanner || form.banners.length >= MAX_BANNERS}
                className="mt-3 flex items-center gap-2 px-4 py-2 border rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                <MdOutlineFileUpload className="text-[20px]" />
                {uploadingBanner
                  ? "Mengupload banner..."
                  : "Upload Banner (multiple)"}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="flex items-center justify-end gap-3 mt-6">
        {!isEditMode ? (
          <button
            type="button"
            onClick={() => setIsEditMode(true)}
            className="py-2 px-4 bg-gray-800 text-white rounded-md hover:bg-black"
          >
            Ubah Settingan
          </button>
        ) : (
          <>
            <button
              type="button"
              onClick={() => {
                setIsEditMode(false);
                setErrors({});
                setForm(mapSettingToForm(dataSetting));
              }}
              className="py-2 px-4 border rounded-md hover:bg-gray-100"
            >
              Batal
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isLoading || uploadingLogo || uploadingBanner}
              className="py-2 px-4 bg-gray-800 text-white rounded-md hover:bg-black disabled:opacity-60"
            >
              {isLoading ? "Menyimpan..." : "Simpan"}
            </button>
          </>
        )}
      </div>

      <Snackbar
        open={Boolean(errorMessage)}
        autoHideDuration={4000}
        onClose={() => setErrorMessage("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity="error" onClose={() => setErrorMessage("")}>
          {errorMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={Boolean(successMessage)}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity="success" onClose={() => setSuccessMessage("")}>
          {successMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default PSEFSetting;
